/**
 * WHICH FORMS A DISPATCH IS FOR (field feedback 3).
 * =================================================
 *
 * `JobTypeScreen` used to map over the entire global registry, so all five schemas (install,
 * maintenance, post-install, provisioning, site-prep) appeared on every job — a technician
 * dispatched for Maintenance was offered the install form and four others alongside the one they
 * came to fill. The intervention type was already in scope on that screen and was read only to
 * print the summary line. This module is the predicate it was missing.
 *
 * ── THE TABLE IS AN OWNER CALL, AND THIS IS THE REVIEWABLE LIST ─────────────────────────────────
 *
 * `Intervention_Type__c` is a 43-value picklist; the measured distribution on the 25,080-row
 * actionable Dev1 set (2026-09-03, recorded on the `intervention.type` catalog entry in
 * `src/forms/prefill.ts`) is: Maintenance 12,885, Permit 3,350, Permanent Removal 2,406,
 * Aesthetics 1,392, Survey 1,171, Premier Survey 889, Repair 780, Installation 779,
 * Provisioning 337, Site Preparation 188, Survey Rework 123, and 17 further values below 100.
 * Only the type→form pairs that are UNAMBIGUOUS by name are mapped; everything else falls back
 * to showing all forms. Each row below is a decision the owner can veto individually:
 *
 *   | `Intervention_Type__c` value | forms shown  | why                                            |
 *   |------------------------------|--------------|------------------------------------------------|
 *   | `Maintenance`                | maintenance  | same word; 12,885 rows                         |
 *   | `Installation`               | install      | same word; the type `deriveVisitKind` maps     |
 *   | `Provisioning`               | provisioning | same word; the type `deriveVisitKind` maps     |
 *   | `Site Preparation`           | site-prep    | same concept; the Site Prep Supplier Form      |
 *   | `Post-Install`               | post-install | same word. NOT in the Dev1 top values — kept   |
 *   |                              |              | defensively so the value maps if it occurs     |
 *
 * DELIBERATELY NOT MAPPED, each because the mapping would be a guess:
 *
 *  · **Survey / Premier Survey / Survey Rework / Survey for Relocation / Survey for Upsize** —
 *    2,272 actionable rows with NO form of their own: survey answers live on `Survey__c`, which
 *    has no supplier linkage and is unreachable (see the header of `compose/intervention.ts`).
 *    Guessing site-prep for them would point 2,272 dispatches at a form about concrete bases.
 *  · **Permit (3,350), Permanent Removal (2,406), Aesthetics (1,392), Repair (780)** — none of
 *    the five schemas is named for these. `Repair`→maintenance is plausible and is exactly the
 *    kind of call the owner makes, not this file.
 *  · **`Reinstallation` and the long tail** — same reasoning `deriveVisitKind` records for
 *    refusing to map `Reinstallation` to `Installation`: it LOOKS like it should map and must
 *    not, because it is a distinct type.
 *
 * ── THE FALLBACK IS MANDATORY ───────────────────────────────────────────────────────────────────
 *
 * An unmapped type, an absent type, or no dispatch at all (the typed-KID path, PRD §4.3) shows
 * ALL forms, so a technician is never locked out of the form they actually need. `expectsKiosk`
 * is the precedent: it warns, it does not hide — this module hides only where the mapping is
 * certain, and the certain cases cover Maintenance + Installation + Provisioning +
 * Site Preparation = 14,189 of 25,080 actionable rows (56.6%).
 */

import type { FormSchema } from '@/forms';
import type { JobDispatch } from './flow';

/**
 * The owner-reviewable type→forms table. Keys are exact `Intervention_Type__c` values (the field
 * is a restricted picklist, so exact matching is correct — normalising here would re-introduce
 * the guessing this table exists to avoid); values are schema ids from `src/forms/schemas`.
 */
export const FORM_ELIGIBILITY: Readonly<Record<string, readonly string[]>> = {
  Maintenance: ['maintenance'],
  Installation: ['install'],
  Provisioning: ['provisioning'],
  'Site Preparation': ['site-prep'],
  'Post-Install': ['post-install'],
};

/**
 * The forms to offer for this dispatch.
 *
 * Returns `schemas` unfiltered — the mandatory show-all fallback — when there is no dispatch, the
 * type is absent, the type is unmapped, or the table names only schemas that do not exist (a
 * stale id must degrade to showing everything, never to an empty screen).
 */
export function eligibleSchemas(
  schemas: readonly FormSchema[],
  dispatch: JobDispatch | undefined,
): readonly FormSchema[] {
  const type = dispatch?.intervention.type;
  if (type === null || type === undefined) return schemas;

  const eligible = FORM_ELIGIBILITY[type];
  if (eligible === undefined) return schemas;

  const filtered = schemas.filter((schema) => eligible.includes(schema.id));
  return filtered.length > 0 ? filtered : schemas;
}

/** True when {@link eligibleSchemas} actually narrowed the list, so the UI can say it did. */
export function isFiltered(
  schemas: readonly FormSchema[],
  dispatch: JobDispatch | undefined,
): boolean {
  return eligibleSchemas(schemas, dispatch).length < schemas.length;
}
